/* eslint-disable react/prop-types */
/**
 * NextStepsDialog — post-sign "what next" dialog, extracted VERBATIM from
 * App.js (Dashboard), V2 Notes migration Phase 3B-2 Step 2 (Jun 2026).
 * Pure mechanical lift; all state lives in the Dashboard and flows via ctx.
 */
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";

export const NextStepsDialog = ({ ctx }) => {
  const {
    API,
    formTemplates,
    getSchedulersForProvider,
    nextStepsCptBlocked,
    refreshNotes,
    refreshTodos,
    selectedNote,
    setNotBillableComment,
    setNotBillableReason,
    setNotBillableTargetNote,
    setScheduleNextData,
    setSelectedNote,
    setShowNextStepsDialog,
    setShowNotBillableDialog,
    setShowNoteDetail,
    setShowScheduleNextDialog,
    showNextStepsDialog,
    updateNoteStatusOptimistically,
    user,
  } = ctx;

  return (
      <Dialog open={showNextStepsDialog} onOpenChange={(open) => {
        setShowNextStepsDialog(open);
        if (!open) {
          // Clean up selectedNote when Next Steps dialog closes
          setSelectedNote(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Note Signed - Next Steps</DialogTitle>
          </DialogHeader>
          {/* Mar 2026 — Heads-up banner when the note's CPT is
              restricted for the client's primary insurance.  Shown for
              both supervisor and provider branches.  Billing CTA is
              automatically suppressed below; this just explains why. */}
          {nextStepsCptBlocked && (
            <div
              data-testid="next-steps-cpt-blocked-banner"
              className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
            >
              <strong>CPT restricted:</strong> This note's CPT code is not billable
              to the client's primary insurance.  Mark Complete or Mark Non-Billable
              instead — sending to billing will be rejected.
            </div>
          )}
          <div className="space-y-4">
            {(() => {
              // Check if current user is a supervisor reviewing someone else's note
              const isSupervisorReview = selectedNote &&
                                        selectedNote.provider_id !== user.id &&
                                        selectedNote.assigned_supervisor === user.id;

              if (isSupervisorReview) {
                // Supervisor reviewing supervisee's note.
                //
                // ── Billable flag resolution (Feb 2026 fix) ─────────
                // Match the canonical rule used elsewhere (lines
                // ~51532, ~52556, ~52767): a note is billable if EITHER
                // the note record itself OR its underlying template
                // is marked billable.  Older notes (and notes saved
                // before billable was wired through to the
                // /notes endpoint) frequently had `selectedNote.billable
                // === undefined/false` even when the template was
                // billable — which caused the supervisor's post-sign
                // dialog to show "Mark as Complete" / "Request
                // Corrections" instead of "Send to Billing", silently
                // killing reimbursement on Medicare notes.
                const supervisorTemplate = (selectedNote?.template_id && Array.isArray(formTemplates))
                  ? formTemplates.find(t => t.id === selectedNote.template_id)
                  : null;
                // Mar 2026 — when the note's CPT is restricted for the
                // client's primary insurance (e.g., Medicare/90882),
                // the backend's ready_for_billing guardrail will reject
                // a "Send to Billing" PUT.  Treat the note as non-
                // billable in this dialog so the supervisor sees the
                // correct "Mark Complete" CTA.
                const isBillable = !!(supervisorTemplate?.billable || selectedNote?.billable)
                  && !nextStepsCptBlocked;
                // Mar 2026 — detect templates that still need a client
                // signature AFTER the supervisor signs.  Without this
                // branch, the supervisor's only options were "Send to
                // Billing" / "Mark Complete" — both of which skip the
                // patient signature step entirely, despite the note's
                // backend pipeline (server.py ~L21536) being designed
                // to route to `sent_to_client_for_signature` when the
                // template carries `require_client_signature`.  Same
                // root cause as the "stuck on sent_to_supervisor" bug
                // shipped earlier this session — the FE's UI choices
                // didn't reflect the BE's actual workflow.
                const needsClientSignature = !!supervisorTemplate?.require_client_signature
                  && !selectedNote?.client_signed_at;
                return (
                  <>
                    <p className="text-sm text-gray-600">
                      {needsClientSignature
                        ? 'As the supervisor, you can send this note to the client for signature, or request corrections.'
                        : isBillable
                          ? 'As the supervisor, you can either send this note to billing or request corrections.'
                          : 'As the supervisor, you can mark this note complete or request corrections.'}
                    </p>

                    <div className="space-y-2">
                      {needsClientSignature ? (
                        <Button
                          className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="supervisor-next-step-send-to-client"
                          onClick={async () => {
                            try {
                              updateNoteStatusOptimistically(selectedNote.id, 'sent_to_client_for_signature');
                              // No explicit status here either — the
                              // backend's post-supervisor-sign override
                              // already handles the routing.  We just
                              // nudge it by re-touching supervisor_signed_at
                              // so the post-update block fires.  But the
                              // simpler / more reliable path is to PUT
                              // the status directly so we don't depend
                              // on a noop write to trigger the override.
                              await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'sent_to_client_for_signature' });
                              toast.success('Note sent to client for signature');
                              setShowNextStepsDialog(false);
                              setShowNoteDetail(false);
                              // Mar 2026 — Refresh server-side caches
                              // (notes list, todo list) so the
                              // Documents tab and the supervisor's
                              // task list reflect the just-sent
                              // status without requiring a tab toggle.
                              refreshNotes(); refreshTodos();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to send to client');
                              refreshNotes(); refreshTodos();
                            }
                          }}
                        >
                          📨 Send to Client for Signature
                        </Button>
                      ) : isBillable ? (
                        <Button
                          className="w-full justify-start bg-teal-600 hover:bg-teal-700 text-white"
                          onClick={async () => {
                            try {
                              updateNoteStatusOptimistically(selectedNote.id, 'signed_ready_for_billing');
                              await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'signed_ready_for_billing' });
                              toast.success('Note sent to billing');
                              setShowNextStepsDialog(false);
                              setShowNoteDetail(false);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to update note status');
                              refreshNotes(); refreshTodos();
                            }
                          }}
                        >
                          💰 Send to Billing
                        </Button>
                      ) : (
                        <Button
                          className="w-full justify-start bg-green-600 hover:bg-green-700 text-white"
                          onClick={async () => {
                            try {
                              updateNoteStatusOptimistically(selectedNote.id, 'completed');
                              await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'completed' });
                              toast.success('Note marked as complete');
                              setShowNextStepsDialog(false);
                              setShowNoteDetail(false);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to update note status');
                              refreshNotes(); refreshTodos();
                            }
                          }}
                        >
                          ✅ Mark as Complete
                        </Button>
                      )}

                      <Button
                        className="w-full justify-start"
                        variant="outline"
                        onClick={() => {
                          setShowNextStepsDialog(false);
                          // Show corrections dialog
                          const reason = prompt('What corrections are needed?');
                          if (reason) {
                            const additionalFields = {
                              needs_corrections_reason: reason,
                              corrections_requested_by: user.id,
                              corrections_requested_at: new Date().toISOString()
                            };
                            updateNoteStatusOptimistically(selectedNote.id, 'needs_corrections', additionalFields);
                            axios.put(`${API}/notes/${selectedNote.id}`, {
                              status: 'needs_corrections',
                              ...additionalFields
                            }).then(() => {
                              toast.success('Corrections requested');
                              setShowNoteDetail(false);
                            }).catch(() => {
                              toast.error('Failed to request corrections');
                            });
                          }
                        }}
                      >
                        🔄 Request Corrections
                      </Button>

                      {/* Mar 2026 — Mark Non-Billable escape hatch
                          for supervisors signing CPT-restricted /
                          duplicate notes.  Opens the shared dialog
                          with `selectedNote` as the target.  Default
                          reason is `cpt_restricted_for_insurance`
                          when the validator says primary blocks. */}
                      <Button
                        className="w-full justify-start border-slate-500 text-slate-700 hover:bg-slate-50"
                        variant="outline"
                        data-testid="supervisor-next-step-mark-not-billable"
                        onClick={() => {
                          setShowNextStepsDialog(false);
                          setNotBillableTargetNote(selectedNote);
                          setNotBillableReason(nextStepsCptBlocked ? 'cpt_restricted_for_insurance' : '');
                          setNotBillableComment('');
                          setShowNotBillableDialog(true);
                        }}
                      >
                        🚫 Mark Non-Billable
                      </Button>

                      <Button
                        className="w-full justify-start"
                        variant="ghost"
                        onClick={() => {
                          setShowNextStepsDialog(false);
                          setShowNoteDetail(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                );
              } else {
                // Provider signing their own note or other scenarios - show all options
                //
                // Mar 2026 — surface a "Send to Client for Signature"
                // CTA when the template requires a client signature
                // AND no supervisor co-sign is still pending.  Without
                // this branch, the provider's only paths forward
                // bypass the patient signature step entirely (Send to
                // Supervisor / Send to Billing / Mark Complete).  When
                // a supervisor IS still required, we keep the existing
                // "Send for Additional Signatures" path so the note
                // can finish its review loop first.
                const providerTemplate = (selectedNote?.template_id && Array.isArray(formTemplates))
                  ? formTemplates.find(t => t.id === selectedNote.template_id)
                  : null;
                const templateRequiresClientSig = !!providerTemplate?.require_client_signature;
                const supervisorCoSignDone = !selectedNote?.requires_supervisor_signature
                  || !!selectedNote?.supervisor_signed_at;
                const providerCanSendToClient = templateRequiresClientSig
                  && supervisorCoSignDone
                  && !selectedNote?.client_signed_at;
                return (
                  <>
                    <p className="text-sm text-gray-600">
                      What would you like to do with this note?
                    </p>

                    <div className="space-y-2">
                      {providerCanSendToClient && (
                        <Button
                          className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="provider-next-step-send-to-client"
                          onClick={async () => {
                            try {
                              updateNoteStatusOptimistically(selectedNote.id, 'sent_to_client_for_signature');
                              await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'sent_to_client_for_signature' });
                              toast.success('Note sent to client for signature');
                              setShowNextStepsDialog(false);
                              setShowNoteDetail(false);
                              // Mar 2026 — keep parity with the
                              // supervisor branch: refresh server-side
                              // caches so the chart Documents tab
                              // shows the new status immediately.
                              refreshNotes(); refreshTodos();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to send to client');
                              refreshNotes(); refreshTodos();
                            }
                          }}
                        >
                          📨 Send to Client for Signature
                        </Button>
                      )}
                      <Button
                        className="w-full justify-start"
                        variant="outline"
                        onClick={async () => {
                          try {
                            updateNoteStatusOptimistically(selectedNote.id, 'draft');
                            await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'draft' });
                            toast.success('Kept as Draft');
                            setShowNextStepsDialog(false);
                            setShowNoteDetail(false);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to update note status');
                            refreshNotes(); refreshTodos(); // Revert on error
                          }
                        }}
                      >
                        📝 Keep as Draft
                      </Button>

                      <Button
                        className="w-full justify-start"
                        variant="outline"
                        onClick={async () => {
                          try {
                            updateNoteStatusOptimistically(selectedNote.id, 'completed');
                            await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'completed' });
                            toast.success('Marked as Completed');
                            setShowNextStepsDialog(false);
                            setShowNoteDetail(false);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to update note status');
                            refreshNotes(); refreshTodos(); // Revert on error
                          }
                        }}
                      >
                        ✅ Mark as Completed (Non-billable)
                      </Button>

                      <Button
                        className="w-full justify-start"
                        variant="outline"
                        onClick={async () => {
                          try {
                            updateNoteStatusOptimistically(selectedNote.id, 'sent_to_supervisor');
                            await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'sent_to_supervisor' });
                            toast.success('Sent for Additional Signatures');
                            setShowNextStepsDialog(false);
                            setShowNoteDetail(false);
                          } catch (error) {
                            toast.error('Failed to update note status');
                            refreshNotes(); refreshTodos(); // Revert on error
                          }
                        }}
                      >
                        🖊️ Send for Additional Signatures
                      </Button>

                      <Button
                        className="w-full justify-start"
                        variant="outline"
                        disabled={nextStepsCptBlocked}
                        title={nextStepsCptBlocked ? "CPT is restricted for this client's primary insurance — use Mark Non-Billable" : undefined}
                        onClick={async () => {
                          try {
                            updateNoteStatusOptimistically(selectedNote.id, 'signed_ready_for_billing');
                            await axios.put(`${API}/notes/${selectedNote.id}`, { status: 'signed_ready_for_billing' });
                            toast.success('Ready for Billing');
                            setShowNextStepsDialog(false);
                            setShowNoteDetail(false);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to update note status');
                            refreshNotes(); refreshTodos(); // Revert on error
                          }
                        }}
                      >
                        💰 Ready for Billing
                      </Button>

                      {/* Mar 2026 — Mark Non-Billable escape hatch
                          (provider self-sign branch).  Mirrors the
                          supervisor branch — opens the shared dialog
                          targeting `selectedNote` with the
                          CPT-restriction reason pre-selected when
                          applicable. */}
                      <Button
                        className="w-full justify-start"
                        variant="outline"
                        data-testid="provider-next-step-mark-not-billable"
                        onClick={() => {
                          setShowNextStepsDialog(false);
                          setNotBillableTargetNote(selectedNote);
                          setNotBillableReason(nextStepsCptBlocked ? 'cpt_restricted_for_insurance' : '');
                          setNotBillableComment('');
                          setShowNotBillableDialog(true);
                        }}
                      >
                        🚫 Mark Non-Billable
                      </Button>

                      {/* Schedule Next Appointment - Only show if provider has a scheduler */}
                      {(() => {
                        const schedulers = getSchedulersForProvider(user.id);
                        if (schedulers.length === 0) return null;

                        return (
                          <Button
                            className="w-full justify-start bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                            variant="outline"
                            onClick={() => {
                              setShowNextStepsDialog(false);
                              setShowScheduleNextDialog(true);
                              setScheduleNextData({
                                timeframe: '2_weeks',
                                customDate: '',
                                notes: ''
                              });
                            }}
                          >
                            📅 Request Next Appointment Scheduling
                          </Button>
                        );
                      })()}

                      <Button
                        className="w-full justify-start"
                        variant="ghost"
                        onClick={() => {
                          setShowNextStepsDialog(false);
                          setShowNoteDetail(false);
                        }}
                      >
                        Just Close
                      </Button>
                    </div>
                  </>
                );
              }
            })()}
          </div>
        </DialogContent>
      </Dialog>
  );
};
